<template>
  <div class="page-use-case">
    <main>
      <section v-if="!useCase" class="mx-auto max-w-[1200px] px-4 py-16">
        <div class="rounded-3xl bg-gray-50 p-8 text-center ring-1 ring-gray-100">
          <p class="text-xl font-semibold text-ink">Use case not found.</p>
          <p class="mt-2 text-sm text-muted">Pick another category to keep planning.</p>
          <RouterLink class="mt-4 inline-flex items-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark" to="/use-cases">
            ← Back to use cases
          </RouterLink>
        </div>
      </section>

      <section v-else class="relative overflow-hidden" :style="{ background: useCase.bg || '#f8fafc' }">
        <div class="absolute inset-0 bg-gradient-to-r from-white/40 via-transparent to-white/30"></div>
        <div class="relative mx-auto max-w-[1200px] px-4 py-16 lg:px-8 lg:py-20">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <RouterLink class="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-brandDark" to="/use-cases">
              ← Back to use cases
            </RouterLink>
            <span class="inline-flex items-center gap-2 rounded-full bg-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-brand shadow-card ring-1 ring-white/60">
              {{ useCase.badge }}
            </span>
          </div>
          <div class="mt-6 grid gap-8 lg:grid-cols-[1.3fr_0.7fr] lg:items-center">
            <div class="space-y-4">
              <div class="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-sm font-semibold text-brand shadow-card ring-1 ring-white/60">
                <span>{{ useCase.icon }}</span>
                <span>{{ useCase.title }}</span>
              </div>
              <h1 class="text-3xl font-semibold leading-tight text-ink sm:text-4xl">{{ useCase.title }} on Invi8</h1>
              <p class="text-lg text-muted">{{ useCase.copy }}</p>
              <div class="flex flex-wrap gap-3">
                <span class="rounded-full bg-white px-4 py-2 text-sm font-semibold text-ink shadow-card ring-1 ring-gray-200">Instant invites</span>
                <span class="rounded-full bg-white px-4 py-2 text-sm font-semibold text-ink shadow-card ring-1 ring-gray-200">Vendor holds</span>
                <span class="rounded-full bg-white px-4 py-2 text-sm font-semibold text-ink shadow-card ring-1 ring-gray-200">Clear RSVPs</span>
              </div>
              <div class="flex flex-wrap gap-3">
                <RouterLink class="rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark" to="/categories">
                  Browse templates
                </RouterLink>
                <RouterLink class="rounded-full bg-white px-5 py-3 text-sm font-semibold text-ink shadow-card ring-1 ring-gray-200 transition hover:-translate-y-0.5" to="/vendors">
                  Find vendors
                </RouterLink>
              </div>
            </div>
            <div class="rounded-3xl bg-white/80 p-6 shadow-card ring-1 ring-gray-100 backdrop-blur">
              <p class="text-sm font-semibold text-ink">Highlights</p>
              <ul class="mt-3 space-y-2 text-sm text-muted">
                <li v-for="(item, index) in useCase.points" :key="index" class="flex items-start gap-2">
                  <span class="mt-1 h-1.5 w-1.5 rounded-full bg-brand"></span>
                  <span>{{ item }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section v-if="useCase" class="mx-auto max-w-[1200px] px-4 py-12 lg:px-8">
        <div class="grid gap-6 lg:grid-cols-2">
          <div class="rounded-3xl bg-white p-6 shadow-card ring-1 ring-gray-100">
            <div class="flex items-center gap-2">
              <span class="rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand">How it works</span>
            </div>
            <ul class="mt-4 space-y-3 text-sm text-muted">
              <li v-for="(step, index) in useCase.howItWorks" :key="index" class="flex items-start gap-3">
                <span class="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand/10 text-xs font-semibold text-brand">{{ index + 1 }}</span>
                <span>{{ step }}</span>
              </li>
            </ul>
          </div>
          <div class="rounded-3xl bg-gray-900 p-6 text-white shadow-card ring-1 ring-gray-800">
            <div class="flex items-center gap-2">
              <span class="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-200">Benefits</span>
            </div>
            <ul class="mt-4 space-y-3 text-sm text-gray-300">
              <li v-for="(benefit, index) in useCase.benefits" :key="index" class="flex items-start gap-3">
                <span class="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-white">✓</span>
                <span>{{ benefit }}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import { useCases } from '@/data/useCases'

const route = useRoute()
const useCase = computed(() => useCases.find(item => item.slug === route.params.slug))
</script>
